// The tick-box result sheet: what gets printed, and how a photo of it is read back.
//
// WHY A PRINTED SHEET AND NOT HANDWRITING. Reading "1-0" or "½" written by a
// dozen different hands needs a trained model on a server. Deciding whether a
// box at a known place is empty or crossed needs arithmetic, so it runs on the
// phone — no server, no API key, no cost per photo, and it works with no signal.
// The price is that only sheets printed here can be read.
//
// HOW IT IS READ.
//  1. Each page carries a QR code in every corner. All four hold the same page
//     identity (session token, round, page, a fingerprint of that page's boards)
//     plus which corner they are. jsQR finds them; the caller passes it in as
//     `decodeQr`, which keeps this module free of the DOM and testable in node.
//  2. The four QR centres, at known positions on the sheet, give a homography
//     from sheet millimetres to photo pixels — so a photo taken at an angle, or
//     upside down, still lands every box where it is.
//  3. For each box, the share of dark pixels inside it is measured against the
//     paper around it, so a shadow across the page does not read as a cross.
//
// Everything printed and everything read uses the constants below. Change the
// layout and the reader follows; that is why both halves live in one file.

export const SHEET_VERSION = 'ZZRS1';

// Millimetres. A4 is 210 x 297; the app's print stylesheet keeps a 1.5 cm margin,
// and the height stays 2 mm short of the printable 267 so the page never spills.
export const SHEET_W = 180;
export const SHEET_H = 265;

export const ROWS_PER_PAGE = 17;
export const RESULTS = ['1-0', '0.5-0.5', '0-1'];

const QR_SIZE = 22;
export const CORNERS = {
    tl: { x: 14, y: 14 },
    tr: { x: SHEET_W - 14, y: 14 },
    bl: { x: 14, y: SHEET_H - 14 },
    br: { x: SHEET_W - 14, y: SHEET_H - 14 }
};

const FIRST_ROW_Y = 45;
const ROW_H = 11;
const BOX = 9;
const BOX_X = [86, 102, 118];
const BOARD_X = 17;
const WHITE_X = 27;
const BLACK_X = 128;
const NAME_CHARS = 26;

// Reading thresholds, as a share of the box's inner area that is dark.
// An empty box reads ~0, a pen cross ~0.2-0.3, a filled-in box close to 1.
export const MARK_MIN = 0.04;     // below this the box is empty
export const STRONG_MARK = 0.08;  // below this a mark is too faint to trust
export const FILLED = 0.5;        // at or above this the box was filled in (a correction)
const DARK_FRACTION = 0.7;        // a pixel is dark below 70% of the local paper brightness
const INNER = 2.5;                // half-width of the sampled area: 5 mm of a 9 mm box
const AROUND = 6;                 // half-width of the window the paper brightness is taken from

const isByeId = (id) => id === 'BYE' || id === 'WO';

function escapeXml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c]));
}

function clip(name) {
    const s = String(name || '').replace(/\s+/g, ' ').trim();
    return s.length > NAME_CHARS ? s.slice(0, NAME_CHARS - 1) + '…' : s;
}

// ---- Pages ------------------------------------------------------------------

/** The boards that take a result, in board order. Byes are not printed. */
export function scorableBoards(pairings) {
    return (Array.isArray(pairings) ? pairings : [])
        .filter(p => p && !isByeId(p.whiteId) && !isByeId(p.blackId));
}

/** Scorable boards dealt into pages of ROWS_PER_PAGE. */
export function sheetPages(pairings) {
    const boards = scorableBoards(pairings);
    const pages = [];
    for (let i = 0; i < boards.length; i += ROWS_PER_PAGE) pages.push(boards.slice(i, i + ROWS_PER_PAGE));
    return pages;
}

/**
 * Six hex digits identifying who plays where on a page. A sheet printed before
 * the arbiter edited the pairings carries the old value, so the phone can refuse
 * it instead of putting a result on the wrong game.
 */
export function pageFingerprint(boards) {
    let h = 0x811c9dc5;
    const text = (boards || []).map(b => `${b.board}:${b.whiteId}|${b.blackId};`).join('');
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0').slice(0, 6);
}

export function encodeSheetPayload({ token, roundNo, page, fp }, corner) {
    return [SHEET_VERSION, token, roundNo, page, fp, corner].join('|');
}

export function parseSheetPayload(text) {
    const parts = String(text || '').split('|');
    if (parts.length !== 6 || parts[0] !== SHEET_VERSION) return null;
    const [, token, round, page, fp, corner] = parts;
    const roundNo = Number(round);
    const pageNo = Number(page);
    if (!token || !Number.isInteger(roundNo) || !Number.isInteger(pageNo) || pageNo < 0) return null;
    if (!/^[0-9a-f]{6}$/.test(fp) || !Object.prototype.hasOwnProperty.call(CORNERS, corner)) return null;
    return { token, roundNo, page: pageNo, fp, corner };
}

// ---- Printing ---------------------------------------------------------------

export function rowCenterY(row) {
    return FIRST_ROW_Y + row * ROW_H + ROW_H / 2;
}

export function boxCenter(row, col) {
    return { x: BOX_X[col], y: rowCenterY(row) };
}

/**
 * One printable page as an SVG string, sized in millimetres.
 *
 * options: { title, subtitle, labels: { board, white, black, instructions: [line, line] },
 *            token, roundNo, page, boards, qrDataUrl(text) -> data: URL }
 * `boards` are scan-session pairings ({ board, whiteName, blackName, whiteId, blackId }).
 */
export function buildSheetSvg({ title, subtitle, labels, token, roundNo, page, boards, qrDataUrl }) {
    const fp = pageFingerprint(boards);
    const l = labels || {};

    const codes = Object.entries(CORNERS).map(([corner, c]) => {
        const href = qrDataUrl(encodeSheetPayload({ token, roundNo, page, fp }, corner));
        return `<image href="${escapeXml(href)}" x="${c.x - QR_SIZE / 2}" y="${c.y - QR_SIZE / 2}" width="${QR_SIZE}" height="${QR_SIZE}" preserveAspectRatio="none" image-rendering="pixelated"/>`;
    }).join('');

    const instructions = (l.instructions || []).map((line, i) =>
        `<text x="8" y="${30 + i * 5}" font-size="3.1">${escapeXml(line)}</text>`).join('');

    const head = `
        <text x="${BOARD_X}" y="42.5" text-anchor="middle" font-size="3" font-weight="700">${escapeXml(l.board || 'Board')}</text>
        <text x="${WHITE_X}" y="42.5" font-size="3" font-weight="700">${escapeXml(l.white || 'White')}</text>
        <text x="${BOX_X[0]}" y="42.5" text-anchor="middle" font-size="3.2" font-weight="700">1-0</text>
        <text x="${BOX_X[1]}" y="42.5" text-anchor="middle" font-size="3.2" font-weight="700">½-½</text>
        <text x="${BOX_X[2]}" y="42.5" text-anchor="middle" font-size="3.2" font-weight="700">0-1</text>
        <text x="${BLACK_X}" y="42.5" font-size="3" font-weight="700">${escapeXml(l.black || 'Black')}</text>
        <line x1="8" y1="${FIRST_ROW_Y}" x2="${SHEET_W - 8}" y2="${FIRST_ROW_Y}" stroke="#888" stroke-width="0.3"/>`;

    const rows = (boards || []).slice(0, ROWS_PER_PAGE).map((b, i) => {
        const bottom = FIRST_ROW_Y + (i + 1) * ROW_H;
        const cy = rowCenterY(i);
        const boxes = BOX_X.map(x =>
            `<rect x="${x - BOX / 2}" y="${cy - BOX / 2}" width="${BOX}" height="${BOX}" fill="none" stroke="#000" stroke-width="0.35"/>`).join('');
        return `
        <text x="${BOARD_X}" y="${cy + 1.3}" text-anchor="middle" font-size="3.8" font-weight="700">${escapeXml(b.board)}</text>
        <text x="${WHITE_X}" y="${cy + 1.2}" font-size="3.4">${escapeXml(clip(b.whiteName))}</text>
        ${boxes}
        <text x="${BLACK_X}" y="${cy + 1.2}" font-size="3.4">${escapeXml(clip(b.blackName))}</text>
        <line x1="8" y1="${bottom}" x2="${SHEET_W - 8}" y2="${bottom}" stroke="#c8c8c8" stroke-width="0.2"/>`;
    }).join('');

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${SHEET_W} ${SHEET_H}" width="${SHEET_W}mm" height="${SHEET_H}mm" font-family="Arial, Helvetica, sans-serif" style="display:block">
        <rect width="${SHEET_W}" height="${SHEET_H}" fill="#fff"/>
        ${codes}
        <text x="${SHEET_W / 2}" y="11" text-anchor="middle" font-size="5" font-weight="700">${escapeXml(title)}</text>
        <text x="${SHEET_W / 2}" y="18" text-anchor="middle" font-size="3.6">${escapeXml(subtitle)}</text>
        ${instructions}
        ${head}
        ${rows}
    </svg>`;
}

// ---- Geometry ---------------------------------------------------------------

// Gaussian elimination with partial pivoting. Returns null for a singular system,
// which is what three corner codes in a straight line would give.
function solveLinear(A, b) {
    const n = b.length;
    const M = A.map((row, i) => [...row, b[i]]);
    for (let col = 0; col < n; col++) {
        let pivot = col;
        for (let r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
        if (Math.abs(M[pivot][col]) < 1e-10) return null;
        [M[col], M[pivot]] = [M[pivot], M[col]];
        for (let r = 0; r < n; r++) {
            if (r === col) continue;
            const f = M[r][col] / M[col][col];
            for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
        }
    }
    return M.map((row, i) => row[n] / row[i]);
}

/**
 * The 3x3 perspective matrix (as 9 numbers) taking four `from` points to four
 * `to` points, or null when they are degenerate (three of them in a line).
 *
 * There is deliberately no three-point fallback. Three points only fix an affine
 * mapping, and a phone photo is never square-on: across an A4 sheet the error
 * reaches several millimetres, enough to sample a row line or a box edge and
 * report a cross on a blank row. Asking for another photo is better than that.
 */
export function solveHomography(from, to) {
    if (from.length < 4 || to.length < 4) return null;
    const A = [];
    const b = [];
    for (let i = 0; i < 4; i++) {
        const { x, y } = from[i];
        const { x: u, y: v } = to[i];
        A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
        A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
    }
    const h = solveLinear(A, b);
    return h ? [...h, 1] : null;
}

export function applyHomography(H, x, y) {
    const w = H[6] * x + H[7] * y + H[8];
    return { x: (H[0] * x + H[1] * y + H[2]) / w, y: (H[3] * x + H[4] * y + H[5]) / w };
}

// The sheet must land in front of the camera, the right way out, and big enough to read.
function plausibleMapping(H, image) {
    const corners = [[0, 0], [SHEET_W, 0], [SHEET_W, SHEET_H], [0, SHEET_H]];
    if (corners.some(([x, y]) => !(H[6] * x + H[7] * y + H[8] > 0))) return false;
    const q = corners.map(([x, y]) => applyHomography(H, x, y));
    if (q.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return false;
    let twice = 0;
    for (let i = 0; i < 4; i++) {
        const j = (i + 1) % 4;
        twice += q[i].x * q[j].y - q[j].x * q[i].y;
    }
    return Math.abs(twice) / 2 >= 0.02 * image.width * image.height;
}

// ---- Finding the corner codes -----------------------------------------------

function cropImage(image, x0, y0, w, h) {
    const left = Math.max(0, Math.floor(x0));
    const top = Math.max(0, Math.floor(y0));
    const width = Math.min(image.width - left, Math.floor(w));
    const height = Math.min(image.height - top, Math.floor(h));
    const data = new Uint8ClampedArray(width * height * 4);
    for (let row = 0; row < height; row++) {
        const start = ((top + row) * image.width + left) * 4;
        data.set(image.data.subarray(start, start + width * 4), row * width * 4);
    }
    return { data, width, height, offsetX: left, offsetY: top };
}

// Paint a found code white, so decoding the same area again finds the next one.
function maskCode(crop, location) {
    const pts = [location.topLeftCorner, location.topRightCorner, location.bottomRightCorner, location.bottomLeftCorner];
    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    const pad = 0.15 * Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
    const x0 = Math.max(0, Math.floor(Math.min(...xs) - pad));
    const x1 = Math.min(crop.width - 1, Math.ceil(Math.max(...xs) + pad));
    const y0 = Math.max(0, Math.floor(Math.min(...ys) - pad));
    const y1 = Math.min(crop.height - 1, Math.ceil(Math.max(...ys) + pad));
    for (let y = y0; y <= y1; y++) {
        crop.data.fill(255, (y * crop.width + x0) * 4, (y * crop.width + x1 + 1) * 4);
    }
}

// Quadrants first — one code each on a sheet that fills the photo — then halves,
// then the whole picture, for a sheet that sits small or off to one side.
const SEARCH_REGIONS = [
    [0, 0, 0.6, 0.6], [0.4, 0, 0.6, 0.6], [0, 0.4, 0.6, 0.6], [0.4, 0.4, 0.6, 0.6],
    [0, 0, 1, 0.6], [0, 0.4, 1, 0.6], [0, 0, 0.6, 1], [0.4, 0, 0.6, 1],
    [0, 0, 1, 1]
];
const DECODES_PER_REGION = 4;

/** Every corner code in the photo, as { payload, x, y } with x/y the code's centre in pixels. */
export function findCornerCodes(image, decodeQr) {
    const found = new Map(); // "<sheet identity>|<corner>" -> code
    const sheetsComplete = () => {
        const counts = {};
        found.forEach(c => { const k = sheetKey(c.payload); counts[k] = (counts[k] || 0) + 1; });
        return Object.values(counts).some(n => n >= 4);
    };

    for (const [fx, fy, fw, fh] of SEARCH_REGIONS) {
        if (sheetsComplete()) break;
        const crop = cropImage(image, fx * image.width, fy * image.height, fw * image.width, fh * image.height);
        for (let attempt = 0; attempt < DECODES_PER_REGION; attempt++) {
            let code;
            try { code = decodeQr(crop); } catch (e) { code = null; }
            if (!code || !code.location) break;
            maskCode(crop, code.location);

            const payload = parseSheetPayload(code.data);
            if (!payload) continue;
            const key = `${sheetKey(payload)}|${payload.corner}`;
            if (found.has(key)) continue;
            const l = code.location;
            const pts = [l.topLeftCorner, l.topRightCorner, l.bottomRightCorner, l.bottomLeftCorner];
            found.set(key, {
                payload,
                x: crop.offsetX + pts.reduce((s, p) => s + p.x, 0) / 4,
                y: crop.offsetY + pts.reduce((s, p) => s + p.y, 0) / 4
            });
            if (sheetsComplete()) break;
        }
    }
    return [...found.values()];
}

function sheetKey(p) {
    return `${p.token}|${p.roundNo}|${p.page}|${p.fp}`;
}

// ---- Reading the boxes ------------------------------------------------------

function luminanceAt(image, x, y) {
    const px = Math.round(x);
    const py = Math.round(y);
    if (px < 0 || py < 0 || px >= image.width || py >= image.height) return null;
    const i = (py * image.width + px) * 4;
    const d = image.data;
    return 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
}

/**
 * The share of a box's inner area that is dark, or null when the box is off the
 * photo or sits in shadow too deep to judge. "Dark" is relative to the paper
 * right around this box, so uneven light across the page does not matter.
 */
export function markRatio(image, H, center) {
    const around = [];
    for (let dy = -AROUND; dy <= AROUND; dy += 0.5) {
        for (let dx = -AROUND; dx <= AROUND; dx += 0.5) {
            const p = applyHomography(H, center.x + dx, center.y + dy);
            const lum = luminanceAt(image, p.x, p.y);
            if (lum === null) return null;
            around.push(lum);
        }
    }
    around.sort((a, b) => a - b);
    const paper = around[Math.floor(around.length * 0.9)];
    if (paper < 40) return null;
    const limit = paper * DARK_FRACTION;

    let dark = 0;
    let total = 0;
    for (let dy = -INNER; dy <= INNER + 1e-9; dy += 0.25) {
        for (let dx = -INNER; dx <= INNER + 1e-9; dx += 0.25) {
            const p = applyHomography(H, center.x + dx, center.y + dy);
            const lum = luminanceAt(image, p.x, p.y);
            if (lum === null) return null;
            total++;
            if (lum < limit) dark++;
        }
    }
    return dark / total;
}

/**
 * Which of a board's three boxes was marked, from their dark shares.
 * Returns null for a blank row, else { index, confident }.
 *
 * The sheet tells players how to correct a mistake: fill the wrong box in, then
 * cross the right one. So a single cross next to filled boxes is a clean read.
 * Anything else with more than one mark is a guess, and says so.
 */
export function decideMarks(ratios) {
    const marked = ratios.map((r, index) => ({ r, index })).filter(m => m.r >= MARK_MIN);
    if (marked.length === 0) return null;

    const open = marked.filter(m => m.r < FILLED);
    if (marked.length === 1) {
        const m = marked[0];
        return { index: m.index, confident: m.r >= STRONG_MARK && m.r < FILLED };
    }
    if (open.length === 1) {
        return { index: open[0].index, confident: open[0].r >= STRONG_MARK };
    }
    const pool = open.length ? open : marked;
    const best = pool.reduce((a, b) => (b.r > a.r ? b : a));
    return { index: best.index, confident: false };
}

/**
 * Reads a photo of a sheet.
 *   image:    { width, height, data } — RGBA, e.g. canvas ImageData
 *   decodeQr: (crop: { width, height, data }) => jsQR-style { data, location } | null
 *
 * Returns { ok: false, code: 'not_found' } unless all four corner codes of one
 * sheet were read (see solveHomography for why three are not enough), else
 * { ok: true, sheet: { token, roundNo, page, fp }, rows: [{ row, result, confident }] }.
 */
export function readResultSheet(image, decodeQr) {
    const codes = findCornerCodes(image, decodeQr);

    const groups = new Map();
    codes.forEach(c => {
        const k = sheetKey(c.payload);
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(c);
    });
    let best = [];
    groups.forEach(g => { if (g.length > best.length) best = g; });
    if (best.length < 4) return { ok: false, code: 'not_found' };

    const H = solveHomography(best.map(c => CORNERS[c.payload.corner]), best.map(c => ({ x: c.x, y: c.y })));
    if (!H || !plausibleMapping(H, image)) return { ok: false, code: 'not_found' };

    const { token, roundNo, page, fp } = best[0].payload;
    const rows = [];
    for (let row = 0; row < ROWS_PER_PAGE; row++) {
        const ratios = BOX_X.map((_, col) => markRatio(image, H, boxCenter(row, col)));
        if (ratios.some(r => r === null)) continue;
        const decision = decideMarks(ratios);
        if (!decision) continue;
        rows.push({ row, result: RESULTS[decision.index], confident: decision.confident });
    }
    return { ok: true, sheet: { token, roundNo, page, fp }, rows };
}
